import "../../candidate-intelligence/results/styles.css";
import type { CandidateListItemView } from "@shared/labs/level-assessment-candidate";
import { ChevronRight, Plus, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { rpcClient } from "@/lib/rpc";
import { ActiveModelProvider, useActiveModel } from "../active-model-context";
import { ModelPicker } from "../model-picker";
import { C, DM, UI } from "../results/ui-tokens";
import { Muted } from "../sets/ui";
import {
  Avatar,
  CandidateKeyframes,
  CandidateStatusChip,
  GhostButton,
  LevelPill,
  PrimaryButton,
  Skeleton,
} from "./candidate-ui";
import { NewCandidateModal } from "./new-candidate-modal";
import { PromoteModal } from "./promote-modal";

/**
 * Tracked candidates — the "candidatos monitorados" surface. Lists the active
 * model's candidates and their current level, and starts a new candidate (from
 * a LinkedIn profile) or promotes an existing one-shot evaluation. Per active
 * model, exactly like the evaluation-set + calibration-data surfaces.
 */
export default function LevelAssessmentCandidates() {
  return (
    <ActiveModelProvider>
      <CandidatesHub />
    </ActiveModelProvider>
  );
}

function CandidatesHub() {
  const { modelId, model } = useActiveModel();
  const [, navigate] = useLocation();

  const [candidates, setCandidates] = useState<CandidateListItemView[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [modal, setModal] = useState<"new" | "promote" | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCandidates(null);
    setListError(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentCandidatesList({
          modelId,
        })) as CandidateListItemView[];
        if (!cancelled) setCandidates(res);
      } catch (e) {
        if (!cancelled) setListError(e instanceof Error ? e.message : "Erro inesperado");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  // Archived candidates leave the default list; a toggle reveals them below
  // the active ones.
  const activeList = candidates?.filter((c) => c.status === "active") ?? [];
  const archivedList = candidates?.filter((c) => c.status === "archived") ?? [];
  const visible = showArchived ? [...activeList, ...archivedList] : activeList;
  const count = activeList.length;

  const actions = (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <GhostButton onClick={() => setModal("promote")}>Promover avaliação</GhostButton>
      <PrimaryButton onClick={() => setModal("new")}>
        <Plus size={16} /> Novo candidato
      </PrimaryButton>
    </div>
  );

  return (
    <div className="ci-stripe ci-stripe-page">
      <CandidateKeyframes />
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1120 }}>
        <div
          style={{
            ...UI,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0",
            textTransform: "uppercase",
            color: C.ink50,
          }}
        >
          Alpha · Labs
        </div>
        <h1
          style={{
            ...DM,
            margin: "6px 0 4px",
            fontSize: 30,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: "-0.015em",
          }}
        >
          Candidatos
        </h1>
        <p
          style={{
            ...UI,
            margin: 0,
            fontSize: 14.5,
            color: C.ink50,
            lineHeight: 1.55,
            maxWidth: 640,
          }}
        >
          Acompanhe como a estimativa de nível de cada candidato evolui conforme novas evidências
          entram, ao longo de rounds de triagem.
        </p>
        <div style={{ marginTop: 18 }}>
          <ModelPicker />
        </div>

        {/* Index head */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 16,
            margin: "26px 0 14px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <h2 style={{ ...DM, margin: 0, fontSize: 19, fontWeight: 700, color: C.ink }}>
              Monitorados
            </h2>
            <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>
              {candidates === null
                ? "carregando…"
                : `${count} candidato${count === 1 ? "" : "s"}${model ? ` · modelo ${model.name}` : ""}`}
              {candidates !== null && archivedList.length > 0 && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={() => setShowArchived((v) => !v)}
                    style={{
                      ...UI,
                      fontSize: 12,
                      color: C.ink50,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      textDecoration: "underline",
                      textUnderlineOffset: 3,
                    }}
                  >
                    {showArchived
                      ? "ocultar arquivados"
                      : `mostrar ${archivedList.length} arquivado${archivedList.length === 1 ? "" : "s"}`}
                  </button>
                </>
              )}
            </span>
          </div>
          {actions}
        </div>

        {listError && <Muted>Falha ao carregar os candidatos: {listError}</Muted>}
        {candidates === null && !listError && <LoadingRows />}
        {candidates && candidates.length === 0 && (
          <EmptyState onNew={() => setModal("new")} onPromote={() => setModal("promote")} />
        )}
        {candidates && candidates.length > 0 && visible.length === 0 && (
          <Muted>
            Nenhum candidato ativo — {archivedList.length} arquivado
            {archivedList.length === 1 ? "" : "s"}. Use "mostrar arquivados" acima para vê-los.
          </Muted>
        )}
        {visible.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {visible.map((c, i) => (
              <CandidateRow
                key={c.id}
                candidate={c}
                first={i === 0}
                last={i === visible.length - 1}
                onOpen={() => navigate(`/labs/level-assessment/candidates/${c.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {modal === "new" && (
        <NewCandidateModal
          modelId={modelId}
          model={model}
          onClose={() => setModal(null)}
          onCreated={(id) => navigate(`/labs/level-assessment/candidates/${id}`)}
        />
      )}
      {modal === "promote" && (
        <PromoteModal
          onClose={() => setModal(null)}
          onPromoted={(id) => navigate(`/labs/level-assessment/candidates/${id}`)}
        />
      )}
    </div>
  );
}

function CandidateRow({
  candidate,
  first,
  last,
  onOpen,
}: {
  candidate: CandidateListItemView;
  first: boolean;
  last: boolean;
  onOpen: () => void;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a row-level button would break the multi-column grid layout; role+key handler provide the a11y path
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto auto auto",
        alignItems: "center",
        gap: 18,
        padding: "16px 20px",
        border: `1px solid ${C.ink10}`,
        borderTop: first ? `1px solid ${C.ink10}` : "none",
        borderRadius: first ? "18px 18px 0 0" : last ? "0 0 18px 18px" : 0,
        background: C.white,
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 13, minWidth: 0 }}>
        <Avatar name={candidate.name} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              ...DM,
              fontWeight: 600,
              fontSize: 15.5,
              color: C.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {candidate.name}
          </div>
          {candidate.subtitle && (
            <div
              style={{
                ...UI,
                fontSize: 13,
                color: C.ink50,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {candidate.subtitle}
            </div>
          )}
        </div>
      </div>
      <CandidateStatusChip status={candidate.status} />
      <span style={{ ...UI, fontSize: 12.5, color: C.ink50, whiteSpace: "nowrap" }}>
        {candidate.roundCount} round{candidate.roundCount === 1 ? "" : "s"}
      </span>
      <LevelPill level={candidate.currentLevel} red={candidate.currentLevel !== null} />
      <ChevronRight size={18} style={{ color: C.ink20 }} />
    </div>
  );
}

function EmptyState({ onNew, onPromote }: { onNew: () => void; onPromote: () => void }) {
  return (
    <div
      style={{
        border: `1px dashed ${C.ink20}`,
        borderRadius: 18,
        background: C.white,
        padding: "56px 30px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: C.red5,
          color: C.red,
          display: "grid",
          placeItems: "center",
          marginBottom: 8,
        }}
      >
        <Users size={24} />
      </div>
      <h3 style={{ ...DM, margin: 0, fontSize: 18, fontWeight: 700, color: C.ink }}>
        Nenhum candidato monitorado ainda
      </h3>
      <p style={{ ...UI, color: C.ink50, maxWidth: 400, margin: "2px 0 16px", fontSize: 14 }}>
        Comece um novo monitoramento com um perfil do LinkedIn, ou promova uma avaliação avulsa já
        feita para acompanhá-la ao longo de rounds.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <GhostButton onClick={onPromote}>Promover avaliação</GhostButton>
        <PrimaryButton onClick={onNew}>
          <Plus size={16} /> Novo candidato
        </PrimaryButton>
      </div>
    </div>
  );
}

function LoadingRows() {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto auto auto",
            alignItems: "center",
            gap: 18,
            padding: "16px 20px",
            border: `1px solid ${C.ink10}`,
            borderTop: i === 0 ? `1px solid ${C.ink10}` : "none",
            borderRadius: i === 0 ? "18px 18px 0 0" : i === 3 ? "0 0 18px 18px" : 0,
            background: C.white,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <Skeleton style={{ width: 40, height: 40, borderRadius: 11 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <Skeleton style={{ width: 160, height: 13 }} />
              <Skeleton style={{ width: 110, height: 11 }} />
            </div>
          </div>
          <Skeleton style={{ width: 60, height: 22, borderRadius: 999 }} />
          <Skeleton style={{ width: 64, height: 12 }} />
          <Skeleton style={{ width: 96, height: 26, borderRadius: 999 }} />
          <div />
        </div>
      ))}
    </div>
  );
}
